import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import {
  ArrowLeft,
  ArrowRight,
  Bot,
  CalendarDays,
  CheckCircle2,
  Database,
  FileText,
  HelpCircle,
  Linkedin,
  ShieldCheck,
  Sparkles,
  X,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { user as userApi } from '../../lib/api';
import { DEFAULT_PRODUCT_GUIDE_LOCALE, PRODUCT_GUIDE_COPY } from './content';
import { useLocale } from '../../i18n/LocaleContext';

const ICONS = {
  sparkles: Sparkles,
  shield: ShieldCheck,
  database: Database,
  linkedin: Linkedin,
  bot: Bot,
  approval: CheckCircle2,
  file: FileText,
  calendar: CalendarDays,
};

export default function ProductGuide() {
  const { user, checkAuth } = useAuth();
  const { locale } = useLocale();
  const navigate = useNavigate();
  const copy = PRODUCT_GUIDE_COPY[locale] || PRODUCT_GUIDE_COPY[DEFAULT_PRODUCT_GUIDE_LOCALE];
  const [open, setOpen] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [saving, setSaving] = useState(false);
  const isEligible = user?.role === 'client' && !user?.impersonated_by;
  const shouldAutoOpen = isEligible && user?.onboarding?.status === 'pending';
  const progressStorageKey = user?.id ? `linirio:product-guide-step:${user.id}` : null;

  useEffect(() => {
    if (shouldAutoOpen) {
      setStepIndex(0);
      setOpen(true);
      return;
    }
    if (!progressStorageKey) return;
    const savedStep = Number(window.sessionStorage.getItem(progressStorageKey));
    if (Number.isInteger(savedStep) && savedStep >= 0 && savedStep < copy.steps.length) {
      setStepIndex(savedStep);
    }
  }, [copy.steps.length, progressStorageKey, shouldAutoOpen]);

  useEffect(() => {
    if (progressStorageKey) {
      window.sessionStorage.setItem(progressStorageKey, String(stepIndex));
    }
  }, [progressStorageKey, stepIndex]);

  if (!isEligible) return null;

  const step = copy.steps[stepIndex];
  const Icon = ICONS[step.icon] || Sparkles;
  const isLast = stepIndex === copy.steps.length - 1;

  const persistAndClose = async (action) => {
    if (saving) return;
    setSaving(true);
    try {
      if (user?.onboarding?.status === 'pending' || action === 'complete') {
        await userApi.updateOnboarding(action);
        await checkAuth();
      }
    } finally {
      setOpen(false);
      setSaving(false);
    }
  };

  const handleOpenChange = (nextOpen) => {
    if (nextOpen) {
      setOpen(true);
      return;
    }
    persistAndClose('dismiss');
  };

  const handleCta = async () => {
    const destination = step.cta?.to;
    await persistAndClose('dismiss');
    if (destination) navigate(destination);
  };

  return (
    <>
      <DialogPrimitive.Root open={open} onOpenChange={handleOpenChange}>
        <DialogPrimitive.Portal>
          <DialogPrimitive.Overlay className="fixed inset-0 z-[70] bg-[#071426]/55 backdrop-blur-[3px] data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=closed]:animate-out data-[state=closed]:fade-out-0" />
          <DialogPrimitive.Content
            className="fixed inset-x-3 top-1/2 z-[71] flex max-h-[calc(100dvh-24px)] -translate-y-1/2 flex-col overflow-hidden rounded-[26px] border border-white/70 bg-[#F8FAFC] shadow-[0_32px_100px_rgba(4,16,35,0.32)] focus:outline-none sm:left-1/2 sm:right-auto sm:h-auto sm:max-h-[min(760px,calc(100dvh-48px))] sm:w-[min(680px,calc(100vw-48px))] sm:-translate-x-1/2"
            aria-describedby="product-guide-description"
            data-testid="product-guide-dialog"
          >
            <div className="relative overflow-hidden border-b border-[#DCE4EE] bg-[#0B1D3A] px-5 pb-6 pt-5 text-white sm:px-8 sm:pb-8 sm:pt-7">
              <div className="absolute -right-16 -top-20 h-52 w-52 rounded-full bg-[#12C8C6]/20 blur-2xl" />
              <div className="absolute -bottom-24 left-20 h-48 w-48 rounded-full bg-[#7BE36A]/15 blur-3xl" />
              <div className="relative flex items-center justify-between gap-4">
                <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#8DE7DF]">{copy.eyebrow}</p>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => persistAndClose('dismiss')}
                    disabled={saving}
                    className="rounded-lg px-2 py-1 text-xs font-medium text-white/70 hover:bg-white/10 hover:text-white disabled:opacity-50"
                  >
                    {copy.skip}
                  </button>
                  <DialogPrimitive.Close asChild>
                    <button
                      type="button"
                      className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-white/15 bg-white/5 text-white/80 hover:bg-white/12 hover:text-white"
                      aria-label={locale === 'cs' ? 'Zavřít průvodce produktem' : 'Close product guide'}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </DialogPrimitive.Close>
                </div>
              </div>

              <div className="relative mt-5 flex items-start gap-4">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-[#7BE36A] to-[#12C8C6] text-[#0B1D3A] shadow-lg shadow-[#12C8C6]/20 sm:h-14 sm:w-14">
                  <Icon className="h-6 w-6 sm:h-7 sm:w-7" />
                </div>
                <div className="min-w-0">
                  <DialogPrimitive.Title className="font-display text-2xl leading-tight sm:text-3xl">
                    {step.title}
                  </DialogPrimitive.Title>
                  <DialogPrimitive.Description id="product-guide-description" className="mt-2 max-w-xl text-sm leading-6 text-white/72 sm:text-[15px]">
                    {step.description}
                  </DialogPrimitive.Description>
                </div>
              </div>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5 sm:px-8 sm:py-7">
              <div className="space-y-3">
                {step.points.map((point, index) => (
                  <div key={point} className="flex gap-3 rounded-2xl border border-[#DCE4EE] bg-white px-4 py-3.5 shadow-[0_8px_24px_-20px_rgba(11,29,58,0.45)]">
                    <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#E6FAF9] text-xs font-semibold text-[#087F7E]">
                      {index + 1}
                    </span>
                    <p className="text-sm leading-6 text-[#334155]">{point}</p>
                  </div>
                ))}
              </div>

              {step.cta && (
                <button
                  type="button"
                  onClick={handleCta}
                  disabled={saving}
                  className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-2xl border border-[#B9EAE5] bg-[#E6FAF9] px-4 py-3 text-sm font-semibold text-[#0B1D3A] transition hover:border-[#12C8C6] hover:bg-[#D7F7F4] disabled:opacity-50 sm:w-auto"
                >
                  {step.cta.label}
                  <ArrowRight className="h-4 w-4" />
                </button>
              )}
            </div>

            <div className="border-t border-[#DCE4EE] bg-white px-5 py-4 sm:px-8">
              <div className="mb-3 flex items-center justify-between gap-4">
                <span className="text-xs font-semibold tabular-nums text-[#64748B]">
                  {stepIndex + 1} / {copy.steps.length}
                </span>
                <div className="flex flex-1 justify-end gap-1.5" aria-hidden="true">
                  {copy.steps.map((item, index) => (
                    <span
                      key={item.key}
                      className={`h-1.5 rounded-full transition-all ${index === stepIndex ? 'w-7 bg-[#12C8C6]' : index < stepIndex ? 'w-3 bg-[#7BE36A]' : 'w-3 bg-[#DCE4EE]'}`}
                    />
                  ))}
                </div>
              </div>
              <div className="flex items-center justify-between gap-3">
                <div>
                  {stepIndex > 0 && (
                    <button
                      type="button"
                      onClick={() => setStepIndex((current) => current - 1)}
                      className="inline-flex items-center gap-2 rounded-xl px-3 py-2.5 text-sm font-semibold text-[#475569] hover:bg-[#F1F5F9]"
                    >
                      <ArrowLeft className="h-4 w-4" />
                      {copy.back}
                    </button>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => isLast ? persistAndClose('complete') : setStepIndex((current) => current + 1)}
                  disabled={saving}
                  className="inline-flex min-w-28 items-center justify-center gap-2 rounded-xl bg-[#0B1D3A] px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-[#0B1D3A]/15 transition hover:bg-[#132C50] disabled:opacity-50"
                  data-testid={isLast ? 'product-guide-finish' : 'product-guide-next'}
                >
                  {isLast ? copy.finish : copy.next}
                  {!isLast && <ArrowRight className="h-4 w-4" />}
                </button>
              </div>
            </div>
          </DialogPrimitive.Content>
        </DialogPrimitive.Portal>
      </DialogPrimitive.Root>

      <button
        type="button"
        onClick={() => setOpen(true)}
        className="fixed bottom-[calc(5.75rem+env(safe-area-inset-bottom))] right-4 z-40 inline-flex h-12 w-12 items-center justify-center rounded-2xl border border-white/80 bg-[#0B1D3A] text-white shadow-[0_16px_38px_rgba(11,29,58,0.3)] transition hover:-translate-y-0.5 hover:bg-[#132C50] focus:outline-none focus:ring-2 focus:ring-[#12C8C6] focus:ring-offset-2 lg:bottom-6 lg:right-6"
        aria-label={copy.reopen}
        title={copy.reopen}
        data-testid="product-guide-trigger"
      >
        <HelpCircle className="h-5 w-5" />
      </button>
    </>
  );
}
